import React, { useState } from 'react';
import { BackButtonCWUI } from '@next-order/next-order-components';
import { useHistory } from 'react-router-dom';

const TermsAndConditions = () => {
  const history = useHistory();
  const [activeSection, setActiveSection] = useState('');

  const handleBack = () => {
    history.goBack();
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  const tableOfContents = [
    { id: 'registration', title: '1. Registration' },
    { id: 'collection-notice', title: '2. Collection Notice' },
    {
      id: 'accuracy-completeness',
      title: '3. Accuracy, completeness and timeliness of information',
    },
    { id: 'promotions-competitions', title: '4. Promotions and competitions' },
    { id: 'orders-processing', title: '5. Orders and processing' },
    { id: 'price-payment', title: '6. Price and Payment' },
    { id: 'customer-reviews', title: '7. Customer Reviews & Ratings' },
    { id: 'linked-sites', title: '8. Linked sites' },
    { id: 'intellectual-property', title: '9. Intellectual property rights' },
    { id: 'warranties-disclaimers', title: '10. Warranties and disclaimers' },
    { id: 'liability', title: '11. Liability' },
    {
      id: 'jurisdiction-governing-law',
      title: '12. Jurisdiction and governing law',
    },
    { id: 'privacy-policy', title: 'Privacy Policy' },
  ];

  return (
    <div className='w-full h-screen bg-white overflow-y-auto relative'>
      <div
        className='relative h-48 bg-cover bg-center bg-no-repeat flex items-center justify-center'
        style={{
          backgroundImage: `url(${process.env.PUBLIC_URL}/terms-and-conditions.webp)`,
        }}
      >
        <BackButtonCWUI
          extendedClassName='fixed top-3.25 left-5 sm:top-3.5 z-50'
          onClick={handleBack}
        />
        <div className='absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center'>
          <h1 className='text-3xl font-bold text-white text-center m-0'>
            Terms & Conditions
          </h1>
        </div>
      </div>

      <div className='p-8 max-w-6xl mx-auto leading-relaxed text-gray-700 text-sm text-justify'>
        <div className='bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8'>
          <h2 className='text-base font-semibold text-gray-800 mb-4 p-0'>
            Table of Contents:
          </h2>
          <ul className='list-none p-0 m-0'>
            {tableOfContents.map((item) => (
              <li key={item.id} className='my-2'>
                <button
                  className={`bg-transparent border-none text-blue-500 no-underline cursor-pointer text-sm text-left py-1 transition-colors duration-200 w-full hover:text-blue-700 hover:underline ${
                    activeSection === item.id
                      ? 'text-blue-700 font-semibold'
                      : ''
                  }`}
                  onClick={() => scrollToSection(item.id)}
                >
                  {item.title}
                </button>
              </li>
            ))}
          </ul>
        </div>
        <p>
          Thank you for visiting our website. This website is owned and operated
          by Next Order Pty Ltd. (ACN 627 375 535). By accessing and/or using
          this website and related services, you agree to these Terms and
          Conditions, which include our Privacy Policy (Terms). You should
          review our Privacy Policy and these Terms carefully and immediately
          cease using our website if you do not agree to these Terms.
        </p>
        <p>In these Terms, 'us', 'we' and 'our' means Next Order Pty Ltd.</p>

        <h2
          id='registration'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          1. Registration
        </h2>
        <p>
          You must be a registered member to make orders, reservations and
          access certain features of our website.
        </p>
        <p>
          When you register and activate your account, you will provide us with
          personal information such as your name, mobile number and address. You
          must ensure that this information is accurate and current. We will
          handle all personal information we collect in accordance with our
          Privacy Policy.
        </p>
        <p>
          When you first place an order on our website, we will register and
          activate your account. You are responsible for keeping your account
          secure and are responsible for all use and activity carried out under
          this account.
        </p>
        <p>To create an account, you must be:</p>
        <ul>
          <li>(a) at least 18 years of age;</li>
          <li>
            (b) possess the legal right and ability to enter into a legally
            binding agreement with us; and
          </li>
          <li>
            (c) agree and warrant to use the website in accordance with these
            Terms.
          </li>
        </ul>

        <h2
          id='collection-notice'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          2. Collection Notice
        </h2>
        <p>
          We collect personal information about you in order to process your
          orders, reservations and for purposes otherwise set out in our Privacy
          Policy.
        </p>
        <p>
          We may disclose that information to third parties that help us deliver
          our services (including information technology suppliers,
          communication suppliers and our Restaurants) or as required by law. If
          you do not provide this information, we may not be able to provide all
          of our services to you.
        </p>
        <p>Our Privacy Policy explains:</p>
        <ul>
          <li>
            (i) how we store and use, and how you may access and correct your
            personal information;
          </li>
          <li>
            (ii) how you can lodge a complaint regarding the handling of your
            personal information; and
          </li>
          <li>(iii) how we will handle any complaint.</li>
        </ul>
        <p>
          By providing your personal information to us, you consent to the
          collection, use, storage and disclosure of that information as
          described in the Privacy Policy and these Terms.
        </p>

        <h2
          id='accuracy-completeness'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          3. Accuracy, completeness and timeliness of information
        </h2>
        <p>
          The information on our website is not comprehensive and is intended to
          provide a summary of the subject matter covered. While we use all
          reasonable attempts to ensure the accuracy and completeness of the
          information on our website, live waiting times displayed for delivery
          and pickup are estimates only as set out in our Delivery Policy.
        </p>
        <p>
          We may, from time to time and without notice, change or add to the
          website (including the Terms) or the information, products or services
          described in it. However, we do not undertake to keep the website
          updated. We are not liable to you or anyone else if errors occur in
          the information on the website or if that information is not
          up-to-date.
        </p>

        <h2
          id='promotions-competitions'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          4. Promotions and competitions
        </h2>
        <p>
          For certain campaigns, promotions or contests, additional terms and
          conditions may apply. If you want to participate in such a campaign,
          promotion or contest, you need to agree to the relevant terms and
          conditions applicable to that campaign, promotion or contest. In case
          of any inconsistency between such terms and conditions and these
          Terms, those terms and conditions will prevail.
        </p>

        <h2
          id='orders-processing'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          5. Orders and processing
        </h2>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.1 Placing your Order
        </h4>
        <p>
          Once you select the Products you wish to order from the menu and
          provide other required information, you will be given the opportunity
          to submit your Order by clicking or selecting the "Order", "Proceed to
          Payment", "Confirm and Pay" or similar button. It is important that
          you check all the information that you enter and correct any errors
          before clicking or selecting this button; once you do so we will
          process your Order and errors cannot be corrected.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.2 Minimum Order Amount
        </h4>
        <p>
          If a minimum order amount is in place, you may not place an order
          until the value of your Order equals or exceeds that amount. The
          minimum order amount must be met after applying any discounts or
          specials that reduce the total Order amount.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.3 Amending or cancelling your Order
        </h4>
        <p>
          Once you submit your Order and your payment has been authorised, you
          will not be entitled to change or cancel your Order online (please
          refer to paragraph 7.5 for details of the process relating to rejected
          Orders). If you wish to change or cancel your Order, you may contact
          the Restaurant directly. However, there is no guarantee that the
          Restaurant will agree to your requests as they may have already
          started to process your Order.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.4 Payment authorisation
        </h4>
        <p>
          Where any payment you make is not authorised, your Order will not be
          processed by or communicated to the Restaurant.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.5 Processing your Order and Restaurant rejections
        </h4>
        <p>
          On receipt of your Order, we will begin processing it by sending it to
          the Restaurant and may notify you by SMS that your Order has been
          received and is being processed. Any confirmation page that you may
          see on the Website merely indicates that your Order has been received
          and is being processed by us, and does not necessarily mean that your
          Order has been accepted by the Restaurant.
        </p>
        <p>
          The restaurant has the discretion to reject Orders at any time because
          they are too busy, due to weather conditions or for any other reason.
          Restaurants are encouraged to accept all Orders and to communicate any
          rejection promptly, and we may notify you by SMS as soon as reasonably
          practicable if a Restaurant rejects your Order.
        </p>
        <p>
          When a Restaurant confirms receipt of the order and expected
          collection or delivery time, may send you an SMS confirmation.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          5.6 Delivery of your Order
        </h4>
        <p>
          Delivery will be provided by the Restaurant. Estimated times for
          deliveries and collections are provided by the Restaurant and are only
          estimates. While the Restaurant will try their best to meet these
          estimates, we make no guarantee that Orders will be delivered or will
          be available for collection within the estimated times. All food
          preparation is the sole responsibility of the Restaurant accepting the
          Order. To the extent permitted by law, including the Australian
          Consumer Law, Next Order accepts no liability associated with food
          preparation or delivery by the Restaurant accepting the Order. The
          Restaurant is responsible for getting your food from the Restaurant to
          your home.
        </p>

        <h2
          id='price-payment'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          6. Price and Payment
        </h2>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.1 Taxes and delivery costs
        </h4>
        <p>
          Prices for individual menu items will be as quoted on the Website in
          Australian dollars. These prices include any applicable taxes but may
          exclude delivery costs (if you opt for delivery instead of collection)
          and any online payment administration charge imposed by the Restaurant
          (if you pay for your Order online). These will be added to the total
          amount due where applicable.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.2 Payment methods
        </h4>
        <p>
          Payment for Orders must be made by an accepted credit or debit card
          through the Website or in cash to the Restaurant at the point of
          collection or delivery to you.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.3 Card payments
        </h4>
        <p>
          If you pay by credit or debit card, you may be required to show the
          card to the Restaurant at the time of delivery as proof of
          identification and so that they can check that the card conforms with
          the receipt data for the Order. Please note that from time to time
          there may be delays with the processing of card payments and
          transactions; this may result in delays in sums being deducted from
          your bank account or charged to your credit or debit card.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.4 Credit and discount vouchers
        </h4>
        <p>
          A credit or discount may apply to your Order if you use a promotional
          voucher or code recognised by the Website and endorsed by the
          Restaurant and you pay for any balance by credit or debit card.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.5 Rejected Orders
        </h4>
        <p>
          Once you have submitted an Order that you are paying for by credit or
          debit card and your payment has been authorised, you will be charged
          the full amount of your Order. If your Order is subsequently rejected
          by the Restaurant (as described in paragraph 6.5 above) or cancelled
          for any other reason, your bank or card issuer will refund the
          relevant amount. However, this may take between 3 to 5 working days
          (or longer, depending on your bank or card issuer). You acknowledge
          and agree that neither we nor the relevant Restaurant will be
          responsible or liable to you in relation to this delay by your bank or
          card issuer in the release of funds back into your account.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          6.6 Ownership of Orders
        </h4>
        <p>
          Your Order remains the property of Next Order until full cleared
          payment for the Order has been received.
        </p>

        <h2
          id='customer-reviews'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          7. Customer Reviews & Ratings
        </h2>
        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>7.1</h4>
        <p>
          You are responsible for review content and ratings. Reviews and
          ratings do not reflect the opinion of Next Order. By submitting a
          review you agree that content provided is true and accurate.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>7.2</h4>
        <p>
          A Review cannot be displayed outside of Next Order managed websites
          without the written consent of Next Order.
        </p>

        <h2
          id='linked-sites'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          8. Linked sites
        </h2>
        <p>
          Our website may contain links to websites operated by third parties.
          Those links are provided for convenience and may not remain current or
          be maintained. Unless expressly stated otherwise, we do not endorse
          and are not for the content on those linked websites and have no
          control over or rights in those linked websites.
        </p>

        <h2
          id='intellectual-property'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          9. Intellectual property rights
        </h2>
        <p>
          (a) Unless otherwise indicated, all content and intellectual property
          we own or license from third parties. All rights, title and interest
          (including copyright, designs, patents, trademarks and other
          intellectual property rights) in this website and in all of the
          material (including all text, graphics, logos, and software) made
          available on this website.
        </p>
        <p>
          (b) Content & intellectual property includes but is not limited to:
          website design, website source code, written content, ordering apps,
          service features, service delivery infrastructure, user interfaces,
          user experience, layouts, "look and feel", logos, graphics, artwork,
          text, images, photographs and pictures.
        </p>
        <p>
          (c) Your use of this website and use of and access to any Content does
          not grant or transfer any rights, title or interest to you in relation
          to this website or the Content. However we do grant you a licence to
          access the website and view the Content on the terms and conditions
          set out in this Agreement.
        </p>
        <p>
          (d) Any reproduction or redistribution of this website or the Content
          is prohibited and may result in civil and criminal penalties. In
          addition, you must not copy the Content to any other server, location
          or support for publication, reproduction or distribution is expressly
          prohibited.
        </p>
        <p>
          (e) Re-publishing content hosted by us, also known as 'hotlinking', is
          not permitted.
        </p>
        <p>
          (f) By submitting any content (including but not limited to reviews,
          ratings, comments, photographs, graphics) to and via Next Order goods
          or services, the Customer grants Next Order the ownership to this
          intellectual property and the right to use it in any manner Next Order
          chooses, to the maximum extent permitted by law. Customers must have
          permission to use content submitted and Next Order is not liable for
          any content submitted having not received authority from its original
          owner.
        </p>
        <p>
          (g) Next Order is not liable for any misprints, type errors, incorrect
          pricing, outdated information and or similar on the Retailer's website
          as this remains the responsibility of the Restaurant.
        </p>
        <p>
          (h) Next Order may display links, images and text throughout any web
          page as seen fit by Next Order for the purposes of promoting the
          Restaurant and/or Next Order. This includes, but is not limited to the
          Next Order logo, taglines and other recognisable symbols.
        </p>

        <h2
          id='warranties-disclaimers'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          10. Warranties and disclaimers
        </h2>
        <p>
          To the maximum extent permitted by law, including the Australian
          Consumer Law, we make no warranties or representations about this
          website or the Content, including but not limited to warranties or
          representations that they will be complete, accurate or up-to-date,
          that access will be uninterrupted or error-free or free from viruses,
          or that this website will be secure.
        </p>
        <p>
          We reserve the right to restrict, suspend or terminate without notice
          your access to this website, any Content, or any feature of this
          website at any time without notice and we will not be responsible for
          any loss, cost, damage or liability that may arise as a result.
        </p>

        <h2
          id='liability'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          11. Liability
        </h2>
        <p>
          To the maximum extent permitted by law, including the Australian
          Consumer Law, in no event shall we be liable for any direct and
          indirect loss, damage or expense – irrespective of the manner in which
          it occurs – which may be suffered due to your use of our website
          and/or the information or materials contained on it, or as a result of
          the inaccessibility of this website and/or the fact that certain
          information or materials contained on it are incorrect, incomplete or
          not up-to-date.
        </p>

        <h2
          id='jurisdiction-governing-law'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          12. Jurisdiction and governing law
        </h2>
        <p>
          Your use of the website and these Terms are governed by the law of
          Victoria and you submit to the non-exclusive jurisdiction of the
          courts exercising jurisdiction in Victoria.
        </p>

        <h2
          id='privacy-policy'
          className='text-2xl font-semibold text-gray-800 mt-8 mb-4 pt-4'
        >
          Privacy Policy
        </h2>
        <p>
          In this Privacy Policy, 'us' 'we' or 'our' means Next Order Pty Ltd
          (ACN 627 375 535) and our related bodies corporate. We are committed
          to respecting your privacy. Our Privacy Policy sets outs out how we
          collect, use, store and disclose your personal information.
        </p>
        <p>
          By providing personal information to us, you consent to our
          collection, use and disclosure of your personal information in
          accordance with this Privacy Policy and any other arrangements that
          apply between us. We may change our Privacy Policy from time to time
          by publishing changes to it on our website. We encourage you to check
          our website periodically to ensure that you are aware of our current
          Privacy Policy.
        </p>
        <p>
          Personal information includes information or an opinion about an
          individual that is reasonably identifiable. For example, this may
          include your name, postcode and contact details. It may also include
          financial information, including your credit card information.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          What personal information do we collect?
        </h4>
        <p>We may collect the following types of personal information:</p>
        <ul className='list-disc list-inside ml-4'>
          <li>name;</li>
          <li>street address;</li>
          <li>telephone number and other contact details;</li>
          <li>credit card information;</li>
          <li>
            your device ID, device type, geo-location information, computer and
            connection information, statistics on page views, traffic to and
            from the sites, IP address and standard web log information;
          </li>
          <li>
            details of the products and services we have provided to you or that
            you have enquired about, including any additional information
            necessary to deliver those products and services and respond to your
            enquiries;
          </li>
          <li>
            any additional information relating to you that you provide to us
            directly through our website or app or indirectly through your use
            of our website or app or online presence or through other websites
            or accounts from which you permit us to collect information;
          </li>
          <li>information you provide to us through reviews; or</li>
          <li>
            any other personal information that may be required in order to
            facilitate your dealings with us.
          </li>
        </ul>
        <p>
          We may collect these types of personal information either directly
          from you, or from third parties. We may collect this information when
          you:
        </p>
        <ul className='list-disc list-inside ml-4'>
          <li>register on our website or app;</li>
          <li>
            communicate with us through correspondence, chats, email, or when
            you share information with us from other social applications,
            services or websites;
          </li>
          <li>
            interact with our sites, services, content and advertising; or
          </li>
          <li>
            invest in our business or enquire as to a potential purchase in our
            business.
          </li>
        </ul>
        <p>
          In addition, when you apply for a job or position with us we may
          collect certain information from you (including your name, contact
          details, working history and relevant records checks) from any
          recruitment consultant, your previous employers and others who may be
          able to provide information to us to assist in our decision on whether
          or not to make you an offer of employment or engage you under a
          contract. This Privacy Policy does not apply to acts and practices in
          relation to employee records of our current and former employees,
          which are exempt from the Privacy Act.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Why do we collect, use and disclose personal information?
        </h4>
        <p>
          We may collect, hold, use and disclose your personal information for
          the following purposes:
        </p>
        <ul className='list-disc list-inside ml-4'>
          <li>to enable you to access and use our website and app;</li>
          <li>
            to operate, protect, improve and optimise our website and app,
            business and our users' experience, such as to perform analytics,
            conduct research and for advertising and marketing;
          </li>
          <li>
            to send you service, support and administrative messages, reminders,
            technical notices, updates, security alerts, and information
            requested by you;
          </li>
          <li>
            to send you marketing and promotional messages and other information
            that may be of interest to you, including information sent by, or on
            behalf of, our business partners that we think you may find
            interesting;
          </li>
          <li>
            to administer rewards, surveys, contests, or other promotional
            activities or events sponsored or managed by us or our business
            partners;
          </li>
          <li>
            to comply with our legal obligations, resolve any disputes that we
            may have with any of our users, and enforce our agreements with
            third parties; and
          </li>
          <li>to consider your employment application.</li>
        </ul>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Do we use your personal information for direct marketing?
        </h4>
        <p>
          We and/or our carefully selected business partners may send you direct
          marketing communications and information about our products. This may
          take the form of emails, SMS, mail or other forms of communication, in
          accordance with the Spam Act and the Privacy Act. You may opt-out of
          receiving marketing materials from us by contacting us at{' '}
          <a
            href='mailto:privacy@nextorder.com.au'
            className='text-blue-500 hover:text-blue-700 underline'
          >
            privacy@nextorder.com.au
          </a>
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          To whom do we disclose your personal information?
        </h4>
        <p>
          We may disclose personal information for the purposes described in
          this privacy policy to:
        </p>
        <ul className='list-disc list-inside ml-4'>
          <li>our employees and related bodies corporate;</li>
          <li>
            third party suppliers and service providers (including providers for
            the operation of our websites and/or our business or in connection
            with providing our products and services to you);
          </li>
          <li>professional advisers, dealers and agents;</li>
          <li>
            payment systems operators (e.g. merchants receiving card payments);
          </li>
          <li>
            our existing or potential agents, business partners or partners;
          </li>
          <li>
            our sponsors or promoters of any competition that we conduct via our
            services;
          </li>
          <li>
            anyone to whom our assets or businesses (or any part of them) are
            transferred;
          </li>
          <li>
            specific third parties authorised by you to receive information held
            by us; and/or
          </li>
          <li>
            other persons, including government agencies, regulatory bodies and
            law enforcement agencies, or as required, authorised or permitted by
            law.
          </li>
        </ul>
        <p>
          We may disclose personal information outside of Australia to third
          party software development companies and cloud providers located in
          India and the United States of America.
        </p>
        <p>
          We will, however, take reasonable steps to ensure that any overseas
          recipient will deal with such personal information in a way that is
          consistent with the Australian Privacy Principles.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Using our website and cookies
        </h4>
        <p>
          We may collect personal information about you when you use and access
          our website. While we do not use browsing information to identify you
          personally, we may record certain information about your use of our
          website, such as which pages you visit, the time and date of your
          visit and the internet protocol address assigned to your computer.
        </p>
        <p>
          We may also use 'cookies' or other similar tracking technologies on
          our website that help us track your website usage and remember your
          preferences. Cookies are small files that store information on your
          computer, mobile phone or other device. They enable the entity that
          put the cookie on your device to recognise you across different
          websites, services, devices and/or browsing sessions. You can disable
          cookies through your internet browser but our websites may not work as
          intended for you if you do so.
        </p>
        <p>
          We may also use cookies to enable us to collect data that may include
          personal information. For example, where a cookie is linked to your
          account, it will be considered personal information under the Privacy
          Act. We will handle any personal information collected by cookies in
          the same way that we handle all other personal information as
          described in this Privacy Policy.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Security
        </h4>
        <p>
          We may hold your personal information in either electronic or hard
          copy form. We take reasonable steps to protect your personal
          information from misuse, interference and loss, as well as
          unauthorised access, modification or disclosure and we use a number of
          physical, administrative, personnel and technical measures to protect
          your personal information. However, we cannot guarantee the security
          of your personal information.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Links
        </h4>
        <p>
          Our website may contain links to websites operated by third parties.
          Those links are provided for convenience and may not remain current or
          be maintained. Unless expressly stated otherwise, we are not
          responsible for the privacy practices of, or any content on, those
          linked websites, and have no control over or rights in those linked
          websites. The privacy policies that apply to those other websites may
          differ substantially from our Privacy Policy, so we encourage
          individuals to read them before using those websites.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Accessing or correcting your personal information
        </h4>
        <p>
          You can access the personal information we hold about you by
          contacting us using the information below. Sometimes, we may not be
          able to provide you with access to all of your personal information
          and, where this is the case, we will tell you why. We may also need to
          verify your identity when you request your personal information.
        </p>
        <p>
          If you think that any personal information we hold about you is
          inaccurate, please contact us and we will take reasonable steps to
          ensure that it is corrected.
        </p>

        <h4 className='text-base font-semibold text-gray-800 mt-6 mb-3'>
          Making a complaint
        </h4>
        <p>
          If you think we have breached the Privacy Act, or you wish to make a
          complaint about the way we have handled your personal information, you
          can contact us using the details set out below. Please include your
          name, email address and/or telephone number and clearly describe your
          complaint. We will acknowledge your complaint and respond to you
          regarding your complaint within a reasonable period of time. If you
          think that we have failed to resolve the complaint satisfactorily, we
          will provide you with information about the further steps you can
          take.
        </p>
        <p>
          For further information about our Privacy Policy or practices, or to
          access or correct your personal information, or make a complaint,
          please contact us using the details set out below:
        </p>
        <div className='mt-6 mb-4'>
          <p className='font-bold text-gray-800 mb-2'>Privacy Officer</p>
          <p className='mb-1'>Next Order Pty Ltd</p>
          <p className='mb-1'>2 Malop Street, Geelong</p>
          <p className='mb-4'>
            <a
              href='mailto:privacy@nextorder.com.au'
              className='text-blue-500 hover:text-blue-700 underline'
            >
              privacy@nextorder.com.au
            </a>
          </p>
        </div>
        <p>
          <strong>Effective: 01/01/19</strong>
        </p>
      </div>

      <div className='text-center py-8 mt-8 border-t border-gray-200'>
        <a
          href='/'
          className='text-blue-500 text-base font-semibold no-underline transition-colors duration-200 hover:text-blue-700 hover:underline'
        >
          Back to Home Page
        </a>
      </div>
    </div>
  );
};

export default TermsAndConditions;
